import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";

import {
  formatPricePence,
  type OnlineCourseKitLine,
  type OnlineCourseOption,
} from "@/lib/online-course-pricing";
import {
  getSelectableKits,
  kitPricePence,
  kitSelectionKey,
} from "@/lib/online-course-kit-selection";

const EMPTY_KIT_SELECTIONS: OnlineCourseKitLine[] = [];

type Props = {
  open: boolean;
  option: OnlineCourseOption | null;
  courseTitle: string;
  actionLabel: string;
  initialKitSelections?: OnlineCourseKitLine[];
  onClose: () => void;
  onConfirm: (
    option: OnlineCourseOption,
    kitSelections: OnlineCourseKitLine[],
  ) => void;
};

export default function KitSelectionModal({
  open,
  option,
  courseTitle,
  actionLabel,
  initialKitSelections = EMPTY_KIT_SELECTIONS,
  onClose,
  onConfirm,
}: Props) {
  const kits = useMemo(
    () => (option ? getSelectableKits(option.required_kits) : []),
    [option],
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !option) {
      return;
    }

    setSelectedKeys(
      initialKitSelections.length > 0
        ? initialKitSelections.map(kitSelectionKey)
        : [],
    );
  }, [open, option?.id]);

  if (!option) {
    return null;
  }

  const selectedKits = kits.filter((kit) =>
    selectedKeys.includes(kitSelectionKey(kit)),
  );
  const kitTotal = selectedKits.reduce(
    (sum, kit) => sum + kitPricePence(kit) * (kit.quantity || 1),
    0,
  );
  const totalPence = option.price_pence + kitTotal;

  const toggleKit = (kit: OnlineCourseKitLine) => {
    const key = kitSelectionKey(kit);
    setSelectedKeys((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key],
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-end justify-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-[28px] bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-bold text-slate-950">
                  Add a training kit?
                </Dialog.Title>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Select a kit below, or continue with the course only if you
                  already have products.
                </p>

                <div className="mt-5 space-y-3">
                  {kits.map((kit) => {
                    const key = kitSelectionKey(kit);
                    const checked = selectedKeys.includes(key);

                    return (
                      <label
                        key={key}
                        className={`flex cursor-pointer items-start gap-3 rounded-[20px] border p-4 ${
                          checked
                            ? "border-brand-500 bg-brand-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                          checked={checked}
                          onChange={() => toggleKit(kit)}
                        />
                        <span className="flex-1">
                          <span className="block text-sm font-semibold text-slate-950">
                            {kit.name}
                          </span>
                        </span>
                        <span className="text-sm font-semibold text-slate-950">
                          {formatPricePence(kitPricePence(kit))}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="mt-5 rounded-[20px] bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <div className="flex justify-between gap-4">
                    <span>Course</span>
                    <span>{formatPricePence(option.price_pence)}</span>
                  </div>
                  {selectedKits.length > 0 ? (
                    <div className="mt-2 flex justify-between gap-4">
                      <span>Kits selected</span>
                      <span>{formatPricePence(kitTotal)}</span>
                    </div>
                  ) : null}
                  <div className="mt-2 flex justify-between gap-4 border-t border-slate-200 pt-2 font-semibold text-slate-950">
                    <span>Total</span>
                    <span>{formatPricePence(totalPence)}</span>
                  </div>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:border-slate-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => onConfirm(option, selectedKits)}
                    className="inline-flex items-center justify-center rounded-md bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-500"
                  >
                    {selectedKits.length > 0
                      ? `${actionLabel} with kit${selectedKits.length > 1 ? "s" : ""}`
                      : `${actionLabel} - course only`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
